/**
 * orderMath — a LEGACY ISLAND. Do not build on it.
 *
 * Ordering is layered: raw need → item target → vendor packages. This module
 * predates that split and still speaks the old single-number language, in which
 * one "minimum order quantity" belonged to the item. It does not. A PAR belongs
 * to the ITEM; an MOQ belongs to the VENDOR PRODUCT
 * (`purchase_options.moq_packages`), and an item bought from four vendors has
 * four of them. Counting answers "how much do I have / how much do I need";
 * purchasing answers "which vendor, what package, what minimum".
 *
 * The one implementation of the real thing is
 * `supabase/functions/_shared/parAndMoq.mjs` — `computePurchaseTarget`
 * (item-level floor / increment) and `computeOrderPlan` (vendor MOQ, order
 * increment, package rounding) — mirrored in the browser by
 * `app/javascript/utils/allocationMath.js#computePackages`.
 *
 * Remaining callers, as of this audit (`docs/moq-ownership-audit.md`):
 *
 *   calculateOrderQuantity          → app/javascript/components/Reports.jsx:13
 *                                     (the only production import of this
 *                                     module; the legacy order report)
 *   calculateOrderQuantityFromDeficit → reachable in production ONLY via
 *                                     calculateOrderQuantity above; no direct
 *                                     import outside this module's own tests
 *   normalizeMinimumOrderQty        → DEAD. No caller outside this module and
 *                                     its tests.
 *
 * All three stay exported: the public surface is frozen so the callers this
 * track may not edit keep working. Nothing new should import any of them.
 *
 * The COUNTING surface no longer uses this module at all. CountInterface's
 * need is `max(0, PAR − on hand)` with no minimum anywhere in it, and
 * `utils/sharedOrderMath.js` returns the exact vendor-neutral NEED.
 */

const toFiniteNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const round6 = (value) => Math.round(value * 1e6) / 1e6;

/**
 * @deprecated Dead code — no caller. It encodes "an item has one minimum,
 * defaulting to 1", which is the unit-ambiguous legacy model. The vendor
 * minimum is `purchase_options.moq_packages`, resolved by
 * `_shared/parAndMoq.mjs#resolveMoqPackages`; an item's own floor is
 * `inventory_items.replenishment_minimum_qty`, applied by
 * `_shared/parAndMoq.mjs#computePurchaseTarget`. Exported only to keep this
 * module's public surface stable.
 */
export function normalizeMinimumOrderQty(value) {
  const parsed = toFiniteNumber(value);
  if (!parsed || parsed <= 0) return 1;
  return parsed;
}

/**
 * Order quantity from a shortage: max(need, minimum).
 *
 * The fractional NEED is preserved (round6 trims float noise only — a 9.5 lb
 * shortage is a 9.5 lb order, not 10), and the minimum is a FLOOR, never a
 * rounding step. Whole-package rounding belongs to the vendor layer
 * (allocationMath.computePackages), after a package is chosen.
 *
 * @deprecated Not reachable directly in production — only through
 * `calculateOrderQuantity`, whose sole caller is the legacy order report in
 * Reports.jsx. New code wants `_shared/parAndMoq.mjs#computeOrderPlan`.
 */
export function calculateOrderQuantityFromDeficit(deficit, minOrderQty) {
  const shortage = toFiniteNumber(deficit);
  if (!shortage || shortage <= 0) return 0;

  const minimumOrder = normalizeMinimumOrderQty(minOrderQty);
  return Math.max(round6(shortage), minimumOrder);
}

/**
 * @deprecated Legacy. Kept because Reports.jsx imports it and this track may
 * not edit Reports.jsx. Its `minOrderQty` argument is an item-level minimum,
 * which is not a real fact — see the module header.
 */
export function calculateOrderQuantity(parLevel, onHand, minOrderQty) {
  const par = toFiniteNumber(parLevel) || 0;
  const counted = toFiniteNumber(onHand) || 0;
  return calculateOrderQuantityFromDeficit(par - counted, minOrderQty);
}

// calculateMOQConstrainedOrderQty is gone deliberately: it rounded the order
// up to a MULTIPLE of the MOQ, and a minimum is a floor, never a multiple.
// (`app/javascript/utils/alcoholParRules.mjs` still names it in a comment;
// that reference has been stale since the function was deleted.)
